import React, { useCallback, useEffect, useState } from 'react'
import { Heading, LoadingSpinner, Text } from '../../components/ui'
import { useAuthContext } from '../../contexts/AuthContext'
import { marketingService } from '../../services/marketing'
import type { MarketingGameChampion } from '../../types/marketing'
import { formatGameTime, rankingPlaceLabel } from './gamePuzzle'

export interface GameRankingHistoricoTabProps {
  /** Limite de linhas (ex.: 10 na home) */
  limit?: number
  /** Oculta coluna Melhor tempo */
  hideBestTime?: boolean
}

const GameRankingHistoricoTab: React.FC<GameRankingHistoricoTabProps> = ({
  limit,
  hideBestTime = false,
}) => {
  const { user } = useAuthContext()
  const [champions, setChampions] = useState<MarketingGameChampion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await marketingService.listOverallChampions()
      setChampions(typeof limit === 'number' ? data.slice(0, limit) : data)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar ranking histórico')
      setChampions([])
    } finally {
      setLoading(false)
    }
  }, [limit])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-4">
        <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
          Ranking histórico
        </Heading>
        <Text size="sm" color="muted" className="mt-1">
          Maiores campeões no geral: vitórias (1º lugar em cada edição)
          {hideBestTime ? ' e partidas concluídas.' : ', partidas concluídas e melhor tempo.'}
          {typeof limit === 'number' ? ` Top ${limit}.` : ''}
        </Text>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/30">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Campeão
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Vitórias
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Concluídas
                </th>
                {!hideBestTime && (
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    Melhor tempo
                    <span className="mt-0.5 block text-[10px] font-normal normal-case tracking-normal text-gray-400 dark:text-gray-500">
                      mm:ss:mmm
                    </span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {champions.length === 0 ? (
                <tr>
                  <td
                    colSpan={hideBestTime ? 4 : 5}
                    className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                  >
                    Ainda não há campeões registrados.
                  </td>
                </tr>
              ) : (
                champions.map((entry, idx) => {
                  const isMe = entry.user_id === user?.id
                  const medal = rankingPlaceLabel(idx + 1)

                  return (
                    <tr
                      key={entry.user_id}
                      className={
                        isMe
                          ? 'bg-teal-50 dark:bg-teal-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-900/20'
                      }
                    >
                      <td className="px-4 py-3 text-sm font-extrabold text-[#016B76] dark:text-teal-300">
                        {medal}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {entry.user_name}
                        {isMe ? ' (você)' : ''}
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums text-gray-800 dark:text-gray-100">
                        {entry.victories}
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums text-gray-800 dark:text-gray-100">
                        {entry.completions}
                      </td>
                      {!hideBestTime && (
                        <td className="px-4 py-3 text-sm font-semibold tabular-nums whitespace-nowrap text-gray-800 dark:text-gray-100">
                          {entry.best_time_seconds != null
                            ? formatGameTime(entry.best_time_seconds)
                            : '—'}
                        </td>
                      )}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default GameRankingHistoricoTab
